<template>
  <div class="bg-white rounded-lg border-2 border-gray-900 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xs text-gray-500 uppercase tracking-wider font-medium">
        Audio Exposure
      </h3>
      <div
        v-if="isOverLimit"
        class="flex items-center text-xs text-red-600 font-medium">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        Over Limit
      </div>
    </div>

    <!-- Dual Gauges -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <!-- Headphone Exposure -->
      <div class="text-center">
        <div class="relative inline-block">
          <svg class="w-16 h-16" viewBox="0 0 36 36">
            <path
              class="text-gray-200"
              stroke="currentColor"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path
              :class="getGaugeColor(headphoneDb)"
              stroke="currentColor"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              :stroke-dasharray="`${headphonePercent}, 100`"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-sm font-bold">{{ headphoneDb }}</span>
          </div>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Headphone dB
        </p>
      </div>

      <!-- Environmental Exposure -->
      <div class="text-center">
        <div class="relative inline-block">
          <svg class="w-16 h-16" viewBox="0 0 36 36">
            <path
              class="text-gray-200"
              stroke="currentColor"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path
              :class="getGaugeColor(environmentalDb)"
              stroke="currentColor"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              :stroke-dasharray="`${environmentalPercent}, 100`"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-sm font-bold">{{ environmentalDb }}</span>
          </div>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Environment dB
        </p>
      </div>
    </div>

    <!-- Daily Dose -->
    <div class="mb-4">
      <div class="flex items-center justify-between mb-1">
        <span class="text-xs text-gray-500">Daily Dose (WHO)</span>
        <span :class="['text-xs font-medium', dailyDosePercent > 80 ? 'text-red-600' : (dailyDosePercent > 50 ? 'text-yellow-600' : 'text-green-600')]">
          {{ dailyDosePercent }}%
        </span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div
          :class="[
            'h-2 rounded-full transition-all duration-500',
            dailyDosePercent > 80 ? 'bg-red-500' : (dailyDosePercent > 50 ? 'bg-yellow-500' : 'bg-green-500')
          ]"
          :style="{ width: `${Math.min(dailyDosePercent, 100)}%` }" />
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <p class="text-lg font-bold">
          {{ dangerousMinutes }}
        </p>
        <p class="text-xs text-gray-500">
          min at &gt;80dB
        </p>
      </div>
      <div>
        <p class="text-lg font-bold">
          {{ healthScore }}
        </p>
        <p class="text-xs text-gray-500">
          health score
        </p>
      </div>
    </div>

    <!-- Severity Breakdown -->
    <div v-if="hasSeverityData" class="pt-4 border-t border-gray-100">
      <p class="text-xs text-gray-500 mb-2">
        Loud Events by Severity
      </p>
      <div class="flex space-x-2">
        <div class="flex-1 text-center p-2 bg-yellow-50 rounded">
          <p class="text-sm font-bold text-yellow-700">
            {{ moderateEvents }}
          </p>
          <p class="text-[10px] text-yellow-600">
            80-85dB
          </p>
        </div>
        <div class="flex-1 text-center p-2 bg-orange-50 rounded">
          <p class="text-sm font-bold text-orange-700">
            {{ highEvents }}
          </p>
          <p class="text-[10px] text-orange-600">
            85-90dB
          </p>
        </div>
        <div class="flex-1 text-center p-2 bg-red-50 rounded">
          <p class="text-sm font-bold text-red-700">
            {{ severeEvents }}
          </p>
          <p class="text-[10px] text-red-600">
            &gt;90dB
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioCard',

  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    goal: {
      type: Object,
      default: null
    }
  },

  computed: {
    headphoneDb() {
      return Math.round(this.data?.headphone_average_db || 0)
    },
    environmentalDb() {
      return Math.round(this.data?.environmental_average_db || 0)
    },
    dailyDosePercent() {
      return Math.round(this.data?.daily_dose_percentage || 0)
    },
    dangerousMinutes() {
      return Math.round(this.data?.time_at_dangerous_levels || 0)
    },
    healthScore() {
      return this.data?.health_score || 100
    },
    goalLimit() {
      return this.goal?.target_value || 85
    },
    isOverLimit() {
      return this.headphoneDb > this.goalLimit || this.dailyDosePercent > 100
    },
    headphonePercent() {
      // Scale 0-100dB to 0-100%
      return Math.min((this.headphoneDb / 100) * 100, 100)
    },
    environmentalPercent() {
      return Math.min((this.environmentalDb / 100) * 100, 100)
    },
    moderateEvents() {
      return this.data?.loud_events_by_severity?.moderate || 0
    },
    highEvents() {
      return this.data?.loud_events_by_severity?.high || 0
    },
    severeEvents() {
      return this.data?.loud_events_by_severity?.severe || 0
    },
    hasSeverityData() {
      return this.moderateEvents > 0 || this.highEvents > 0 || this.severeEvents > 0
    }
  },

  methods: {
    getGaugeColor(db) {
      if (db >= 85) return 'text-red-500'
      if (db >= 70) return 'text-yellow-500'
      return 'text-green-500'
    }
  }
}
</script>
